import { useEffect, useMemo } from 'react';
import { Collapse, ToggleButtonGroup } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { ReactComponent as GlueIcon } from '@elementary/common-ui/assets/16x16/glue-metastore.svg';
import { FlexColumn } from '@elementary/common-ui/components';
import {
  AUTH_TYPES,
  DataCatalogForm,
  DataCatalogMetaStore,
  FetchDataStoresApi,
} from 'shared/types';
import { IntegrationError } from 'settings/types';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import DialogToggleButton from '../../../shared/DialogToggleButton';
import GlueDataCatalogForm from './GlueDataCatalogForm';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: DataCatalogForm) => void;
  supportedMetaStores: Array<keyof typeof DataCatalogMetaStore>;
  error?: IntegrationError;
  isLoading?: boolean;
  data?: FetchDataStoresApi['integrations'][0];
};

const DataCatalogDialog = ({
  open,
  onClose,
  onSubmit,
  supportedMetaStores,
  error,
  isLoading,
  data,
}: Props) => {
  const isEdit = !!data?.id;

  const defaultValues = useMemo(() => {
    if (isEdit && data) {
      return {
        selectedMetaStore: data.config.catalog_type,
        id: data.id,
        glueConnectionName: data.name,
        glueRegion: data.config.region,
        ...(data.config.auth.auth_type === AUTH_TYPES.role
          ? {
              glueAuthMethod: AUTH_TYPES.role as const,
              glueRoleArn: data.config.auth.role_arn,
              glueExternalId: data.config.auth.external_id,
            }
          : {
              glueAuthMethod: AUTH_TYPES.access_key as const,
              glueAccessKeyId: data.config.auth.access_key_id,
              glueSecretAccessKey: data.config.auth.secret_access_key,
              glueSessionToken: data.config.auth.session_token,
            }),
      };
    }
    return {
      selectedMetaStore: DataCatalogMetaStore.GLUE, // For now we only support Glue, remove this when we add more metastores
    };
  }, [data, isEdit]);

  const methods = useForm<DataCatalogForm>({ defaultValues });

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect your data catalog"
      description="Give Elementary access to your data catalog."
      isValid={isValid}
      reset={reset}
      onSubmit={handleSubmit(onSubmit)}
      saveButtonText="Connect"
      error={error}
      loading={isLoading}
    >
      <FormProvider {...methods}>
        <ToggleButtonGroup
          value={watch('selectedMetaStore')}
          onChange={(_, val) => {
            setValue('selectedMetaStore', val);
          }}
          fullWidth
          exclusive
        >
          {supportedMetaStores.map((metastore) => (
            <DialogToggleButton key={metastore} value={metastore}>
              <GlueIcon />
              {metastore.charAt(0).toUpperCase() +
                metastore.slice(1).toLowerCase()}
            </DialogToggleButton>
          ))}
        </ToggleButtonGroup>

        <Collapse in={!!watch('selectedMetaStore')}>
          <FlexColumn sx={{ gap: 3 }}>
            {watch('selectedMetaStore') === DataCatalogMetaStore.GLUE && (
              <GlueDataCatalogForm />
            )}
          </FlexColumn>
        </Collapse>
      </FormProvider>
    </IntegrationDialog>
  );
};

export default DataCatalogDialog;
